import React from 'react';
import DocumentPage, {
  Section,
  Paragraph,
  DoDoNot,
  HazardTable,
} from './DocumentPage';

const PortableHandToolsDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Portable Hand Tools – RA & SSOW"
    documentHeading="Risk Assessment 08 (GRA11) & Safe System of Work: Portable Hand Tools"
    meta={[
      ['Document Reference', 'PVS-RAMS-PHT-001'],
      ['Version', '1.0'],
      ['Date of Issue', '17th March 2026'],
      ['Next Review Date', '17th March 2028'],
      ['Area / Location', 'Workshops / Yard area'],
      [
        'Activity',
        'Use of portable hand tools, including powered electrical equipment (e.g. drills) and non-powered tools e.g. spanners, hammers, saws, knives etc. Frequency of use – could be daily or periodic as the work pattern necessitates.',
      ],
      ['Review Period', '24 months'],
    ]}
    acknowledgment={{
      documentName: 'Portable Hand Tools – RA & SSOW',
      documentReference: 'PVS-RAMS-PHT-001',
      processActivity: 'Use of Portable Hand Tools',
      versionNumber: '1.0',
      dateOfIssue: '17th March 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <Section number="1" title="Introduction">
      <Paragraph>
        There are a number of hazards present when using portable hand tools, for example
        entanglement with moving parts, eye injuries from flying debris or flying splinters,
        electrocution / burns / fire from faulty electrical equipment, pressure injection injuries
        from compressed air lines, exposure to electrical and / or pneumatic tools could lead to
        noise induced hearing loss, exposure to vibrating tools could lead to hand arm vibration /
        carpal tunnel syndrome etc.
      </Paragraph>
    </Section>

    <Section number="2" title="Hazard Identification and Risk Assessment">
      <HazardTable
        columns={['Hazard Category', 'Hazards', 'Control Measures Already in Place', 'Additional Control Measures Required']}
        rows={[
          [
            'General Hazards',
            [
              'Entanglement with moving parts.',
              'Eye injuries from flying debris or flying splinters (hot and cold) from tools.',
              'Exposure to noise e.g. pneumatic tools could lead to noise induced hearing loss.',
              'Exposure to vibration could lead to hand arm vibration, carpal tunnel syndrome etc.',
              'Cutting injuries from revolving discs, knives etc.',
              'Tripping over trailing cables, hoses or tools left on the floor.',
            ],
            [
              'Staff have been provided with sufficient information, instruction and training.',
              'PPE is provided and worn specific to the task e.g. eye protection, disposable dust masks and hearing protection.',
              'Where required, workbenches and vices are provided. Vices are secured in place.',
              'Good housekeeping standards are maintained.',
              'Appropriate lighting is provided in all areas.',
              'Tools are maintained in good condition.',
              'Tools are only used for their intended purpose.',
              "Use of any hand tool is for a short duration only on an 'as and when' needed basis and therefore HAVS is not an issue.",
            ],
            'None required.',
          ],
          [
            'Electrical Hazards',
            ['Electrocution / burns / fire from faulty electrical equipment.'],
            ['Staff have been provided with sufficient information, instruction and training.'],
            'Electrical tools should undergo PAT Testing on a regular basis.',
          ],
        ]}
      />
    </Section>

    <Section number="3" title="Safe System of Work">
      <DoDoNot
        doItems={[
          'Carry out a visual check of the equipment before use. For example, when checking electrical equipment, check: bare wires are not visible; the cable covering is not damaged; the plug is in good condition; the casing is not damaged; there are no taped or other non-standard joints in the cables.',
          'Report any defects to your Manager or Supervisor.',
          'Wear impact resistant eye protection (standard spectacles are not adequate) and hearing protection when operating air operated or electrically operated tools e.g. angle grinders, saws, impact wrench, hammer drills, air chisels etc.',
          'Unplug the tool from the power supply before any repair work, servicing or maintenance work is undertaken.',
          'When drilling ensure that you secure non-stable work pieces with a clamp, vice or other adequate holding device.',
          'Use safety knives instead of Stanley knives / Stanley blades.',
          'Take precautions against entanglement with moving parts by tying back long hair, removing loose jewellery etc.',
          'Use the right tool for the job e.g. do not use a wrench as a hammer etc.',
          'CHECK that all persons are clear of the work area.',
          'CHECK the voltage of any electrical equipment matches the power supply. Where possible only use 110 volt, cordless or air tools.',
          'CHECK that the cable has not been pulled out of the cable grip in plugs.',
          'CHECK that equipment is switched off before connecting it to the power supply.',
          'CHECK that safety guards and covers on the equipment are in place and secure.',
          'Keep the area clear of trailing electrical cables and hoses.',
          'Allow fixed speed tools to reach full operating speed before applying them to a surface.',
          "When an automatic cut out operates allow the tool's engine to cool before re-starting.",
          'CHECK that ventilation ducts, grills or holes on the engine casing are not blocked.',
          'Unplug the tool from the power supply before making any adjustments.',
          'Keep hands away from cutting edges while working.',
          'Protect the sharp edges of tools.',
          'CHECK the heads of hammers etc. for security of fixings.',
          'When hammering always hammer away from your body never towards it.',
          'Store tools in the correct tool case etc. when they are not in use.',
        ]}
        doNotItems={[
          'Operate any defective equipment.',
          'Misuse the equipment.',
          'Interfere with any safety device.',
          'Replace a blown fuse with anything other than a fuse of the correct rating. NEVER bypass a fuse or replace it with a length of wire or any other object.',
          'Leave an extension cable fully wound in its drum or on a roll while in use as this may cause overheating.',
          'Use excessive lengths of extension cable.',
          'Allow an electrical cable to lie where it is wet or where it could be damaged e.g. by crushing.',
          'Leave tools, cables etc. in walkways.',
          'Leave unattended tools connected to the power supply.',
          'Leave powered tools (air or electrically powered) operating whilst unattended.',
          'Use an electrical or air powered tool in a damp or flammable atmosphere unless it has been specifically designed for use in those conditions.',
          'Carry a tool with your finger on the operating trigger, button or control.',
          'Carry a tool by its cable.',
          'Tug on a cable to remove a plug from a socket.',
          'Use a tool for an operation it is not designed for e.g. use screwdrivers as chisels or levers.',
          'Tape or wire up split or damaged wooden tool handles, REPLACE the tool or its handle.',
          'Use blunt, worn or damaged bits / accessories.',
          'Change bulbs in inspection lamps unless the lamp has been switched off and unplugged from the power supply.',
          'Hold an unsecured work piece in your hand whilst cutting, drilling, sanding etc.',
          'Use compressed air for cleaning clothing or skin, cleaning up dust or general debris.',
          'Use electrical equipment in areas which have flammable atmospheres.',
          'Kink the hose to stop the air flow for compressed air powered tools.',
          'Point compressed air lines at anyone.',
          'Repair air lines that are under pressure.',
          'Trail air lines / electrical cables along traffic routes.',
        ]}
      />
    </Section>

    <Section number="4" title="Reminder">
      <Paragraph className="font-semibold">
        You have a duty not only to protect your own health and safety but also to ensure that your
        actions do not put others around you at risk.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default PortableHandToolsDoc;
